import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { apiCall } from '../utils/api';

function Admin({ user, onLogout }) {
  const navigate = useNavigate();
  const [inviteCodes, setInviteCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expiresIn, setExpiresIn] = useState(30);

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/projects');
      return;
    }
    
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [codesRes, statsRes] = await Promise.all([
        apiCall(`/api/admin/invite-codes`, {
          
        }),
        apiCall(`/api/admin/stats`, {
          
        })
      ]);

      if (codesRes.ok) {
        const data = await codesRes.json();
        setInviteCodes(data.codes);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const generateInviteCode = async () => {
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('expires_in_days', expiresIn);

      const response = await apiCall(`/api/admin/invite-codes`, {
        method: 'POST',
        body: formData,
        
      });

      if (response.ok) {
        toast.success('Invite code generated!');
        fetchData();
      } else {
        toast.error('Failed to generate code');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setGenerating(false);
    }
  };

  const deleteInviteCode = async (codeId) => {
    if (!window.confirm('Delete this invite code?')) return;

    try {
      const response = await apiCall(
        `/api/admin/invite-codes/${codeId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        toast.success('Invite code deleted');
        fetchData();
      } else {
        toast.error(`Failed to delete: Error ${response.status}`);
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle="Admin Panel" />
        
        <div className="page-content">
          <div className="page-header mb-4">
            <h4>🔐 Admin Dashboard</h4>
            <p className="text-muted">Manage invite codes and system statistics</p>
          </div>

          {/* System Stats */}
          {stats && (
            <div className="row mb-4">
              <div className="col-lg-3 col-md-6">
                <div className="stat-card">
                  <div className="stat-card-icon">👥</div>
                  <h3>{stats.total_users}</h3>
                  <p>Total Users</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stat-card success">
                  <div className="stat-card-icon">📊</div>
                  <h3>{stats.total_projects}</h3>
                  <p>Total Projects</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stat-card info">
                  <div className="stat-card-icon">📱</div>
                  <h3>{stats.total_qrcodes}</h3>
                  <p>Total QR Codes</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stat-card warning">
                  <div className="stat-card-icon">👁️</div>
                  <h3>{stats.total_scans}</h3>
                  <p>Total Scans</p>
                </div>
              </div>
            </div>
          )}

          {/* Invite Codes Management */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">🎟️ Invite Codes</h5>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="number"
                  className="input"
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(parseInt(e.target.value))}
                  min="1"
                  max="365"
                  style={{ width: '100px' }}
                  placeholder="Days"
                />
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>days</span>
                <button
                  className="btn btn-primary"
                  onClick={generateInviteCode}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : '+ Generate Code'}
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr style={{ background: 'var(--gray-100)' }}>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem' }}>INVITE CODE</th>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem' }}>STATUS</th>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem' }}>EXPIRES</th>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem' }}>USED BY</th>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem' }}>CREATED</th>
                    <th style={{ padding: '1rem 0.75rem', fontSize: '0.75rem', textAlign: 'center' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {inviteCodes.map((invite) => {
                    const isExpired = new Date(invite.expires_at) < new Date();
                    const isUsed = invite.used;
                    
                    return (
                      <tr key={invite.id}>
                        <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          <div className="d-flex align-items-center gap-2">
                            <code style={{ background: 'var(--gray-100)', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                              {invite.code}
                            </code>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() => copyCode(invite.code)}
                              aria-label="Copy invite code"
                            >
                              📋
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {isUsed ? (
                            <span className="badge badge-success">USED</span>
                          ) : isExpired ? (
                            <span className="badge badge-danger">EXPIRED</span>
                          ) : (
                            <span className="badge badge-info">ACTIVE</span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                          {new Date(invite.expires_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                          {invite.used_by || '-'}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                          {new Date(invite.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {!isUsed && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteInviteCode(invite.id)}
                              aria-label="Delete invite code"
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {inviteCodes.length === 0 && (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <p className="text-muted">No invite codes generated yet</p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="card mt-4">
            <h5 className="mb-3">📋 How Invite Codes Work</h5>
            <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>Generate invite codes to allow new users to register</li>
              <li>Each code can be used only once</li>
              <li>Codes expire after the specified number of days</li>
              <li>Share codes with trusted users via email or other secure channels</li>
              <li>Used or expired codes cannot be reused</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
